import { useState, useEffect, useCallback } from 'react';
import { fetchProjectById } from '../api/projectsApi';
import type { ProjectWithCustomer } from '../types';

interface UseProjectOptions {
  projectId: string | null;
  autoFetch?: boolean;
}

interface UseProjectReturn {
  project: ProjectWithCustomer | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  notFound: boolean;
}

/**
 * Hook to fetch a single project by ID
 */
export function useProject({
  projectId,
  autoFetch = true,
}: UseProjectOptions): UseProjectReturn {
  const [project, setProject] = useState<ProjectWithCustomer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const loadProject = useCallback(async () => {
    if (!projectId) {
      setProject(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setNotFound(false);

    try {
      const data = await fetchProjectById(projectId);

      if (!data) {
        setNotFound(true);
        setProject(null);
      } else {
        setProject(data);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load project';
      setError(message);
      console.error('[useProject] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (autoFetch && projectId) {
      loadProject();
    }
  }, [autoFetch, projectId, loadProject]);

  return {
    project,
    isLoading,
    error,
    refetch: loadProject,
    notFound,
  };
}